import { useId, useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import FormField from './FormField';
import styles from './Input.module.scss';
import type { InputSize } from './TextInput';

// ============================================
// TYPES
// ============================================

export interface CountryOption {
  /** ISO country code, e.g. "KE" */
  code: string;
  /** Dial code, e.g. "+254" */
  dialCode: string;
  /** Country name */
  name: string;
  /** Flag emoji, e.g. "🇰🇪" */
  flag: string;
}

export interface PhoneInputProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'onChange' | 'value' | 'size'> {
  /** Forwarded ref to the text <input> element */
  ref?: React.Ref<HTMLInputElement>;
  /** Input size variant */
  size?: InputSize;
  /** Label text displayed above the input */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the input (hidden when error is present) */
  helperText?: string;
  /** Error message — replaces helperText, applies error border */
  error?: string;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
  /** Currently selected country (defaults to Kenya) */
  selectedCountry?: CountryOption;
  /** Callback when country code changes */
  onCountryChange?: (country: CountryOption) => void;
  /** The phone number value (without dial code) */
  value?: string;
  /** Callback when phone number text changes */
  onChange?: (value: string) => void;
}

// ============================================
// DEFAULT COUNTRY
// ============================================

const DEFAULT_COUNTRY: CountryOption = {
  code: 'KE',
  dialCode: '+254',
  name: 'Kenya',
  flag: '🇰🇪',
};

// ============================================
// COMPONENT
// ============================================

function PhoneInput({
  ref,
  size = 'md',
  label,
  isRequired = false,
  helperText,
  error,
  fullWidth = true,
  selectedCountry,
  onCountryChange,
  value,
  onChange,
  className,
  id: idProp,
  disabled,
  placeholder = '',
  ...inputProps
}: PhoneInputProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const hasError = Boolean(error);
  const [isFocused, setIsFocused] = useState(false);

  const country = selectedCountry ?? DEFAULT_COUNTRY;

  const describedBy = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const sizeClass = styles[size] ?? styles.md;

  const fieldClassName = [
    sizeClass,
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [
    styles.phoneWrapper,
    isFocused && styles.phoneFocused,
    hasError && styles.phoneError,
    disabled && styles.phoneDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  const handleWrapperFocus = () => {
    setIsFocused(true);
  };

  const handleWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Only lose focus if the new focus target is outside the wrapper
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
      className={fieldClassName}
    >
      <div
        className={wrapperClasses}
        onFocus={handleWrapperFocus}
        onBlur={handleWrapperBlur}
      >
        {/* Country code trigger */}
        <button
          type="button"
          className={styles.phoneCountryTrigger}
          disabled={disabled}
          tabIndex={-1}
          aria-label={`Country code: ${country.dialCode}`}
          onClick={() => onCountryChange?.(country)}
        >
          <span className={styles.phoneFlag} aria-hidden="true">
            {country.flag}
          </span>
          <span className={styles.phoneDialCode}>{country.dialCode}</span>
          <span className={styles.phoneChevron} aria-hidden="true">
            <svg
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {/* Vertical separator */}
        <span className={styles.phoneSeparator} aria-hidden="true" />

        {/* Phone number input */}
        <input
          ref={ref}
          id={id}
          type="tel"
          disabled={disabled}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={styles.phoneInput}
          aria-invalid={hasError || undefined}
          aria-required={isRequired || undefined}
          aria-describedby={describedBy}
          {...inputProps}
        />
      </div>
    </FormField>
  );
}

export default PhoneInput;
